/**
 * OfferContextEditor Component
 *
 * Dynamic form editor for offer context_json based on assignment strategy.
 * Supports MIGRATION, MANUAL, and AUTO_DEFAULT strategies (real DB values).
 * Includes JSON toggle for power users.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Input, Select, TextArea } from '@/components/common/FormField';
import { JsonEditor } from '@/components/common/JsonEditor';
import { useRewardMetadata, getVariantOptions } from '@/app/hooks/useRewardMetadata';
import { useAuth } from '@/app/contexts/AuthContext';
import type {
  OfferContext,
  AssignmentStrategy,
  MigrationContext,
  ManualContext,
  AutoDefaultContext,
} from '@/app/types/offers';
import './OfferContextEditor.scss';

interface OfferContextEditorProps {
  value: OfferContext | null;
  strategy: AssignmentStrategy;
  onChange: (context: OfferContext | null) => void;
}

// Default context values per strategy
const getDefaultContext = (
  strategy: AssignmentStrategy,
  userEmail?: string | null
): OfferContext => {
  switch (strategy) {
    case 'MIGRATION':
      return {
        migration_source: '',
        original_offer_id: '',
        migrated_at: new Date().toISOString(),
      } as MigrationContext;
    case 'MANUAL':
      return {
        assigned_by: userEmail || '',
        reason: '',
        ticket_id: '',
      } as ManualContext;
    case 'AUTO_DEFAULT':
      return {
        source: 'auto_default',
        reward_key: '',
        reward_variant: '',
        assigned_at: new Date().toISOString(),
      } as AutoDefaultContext;
    default:
      return {};
  }
};

export const OfferContextEditor: React.FC<OfferContextEditorProps> = ({
  value,
  strategy,
  onChange,
}) => {
  const [editMode, setEditMode] = useState<'structured' | 'json'>('structured');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [localContext, setLocalContext] = useState<OfferContext | null>(value);
  const { variants } = useRewardMetadata();
  const { userProfile } = useAuth();
  const isInternalUpdate = useRef(false);

  // Reset local context when strategy changes
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    if (!value) {
      setLocalContext(getDefaultContext(strategy, userProfile?.email));
    } else {
      // Ensure source is always "auto_default" for AUTO_DEFAULT strategy
      if (strategy === 'AUTO_DEFAULT') {
        setLocalContext({ ...value, source: 'auto_default' });
      } else {
        // For MANUAL strategy, auto-populate assigned_by if empty and user email is available
        if (strategy === 'MANUAL' && !(value as ManualContext).assigned_by && userProfile?.email) {
          setLocalContext({ ...value, assigned_by: userProfile.email });
        } else {
          setLocalContext(value);
        }
      }
    }
  }, [strategy, value, userProfile?.email]);

  // Sync local changes to parent - always sync when localContext changes
  useEffect(() => {
    // Remove undefined values before comparing/sending (JSON.stringify omits them anyway)
    const cleanLocal = localContext
      ? (Object.fromEntries(
          Object.entries(localContext as Record<string, unknown>).filter(
            ([_, val]) => val !== undefined
          )
        ) as OfferContext)
      : null;

    const cleanValue = value
      ? (Object.fromEntries(
          Object.entries(value as Record<string, unknown>).filter(([_, val]) => val !== undefined)
        ) as OfferContext)
      : null;

    // Compare cleaned versions - if different, update parent
    if (JSON.stringify(cleanLocal) !== JSON.stringify(cleanValue)) {
      onChange(Object.keys(cleanLocal || {}).length > 0 ? cleanLocal : null);
    }
  }, [localContext, onChange, value]);

  const updateField = useCallback(
    (field: string, fieldValue: string | number | boolean | undefined) => {
      isInternalUpdate.current = true;
      setLocalContext((prev) => {
        if (!prev) {
          if (fieldValue === undefined) return {};
          return { [field]: fieldValue } as OfferContext;
        }
        if (fieldValue === undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [field]: _, ...rest } = prev as Record<string, unknown>;
          return rest as OfferContext;
        }
        return { ...prev, [field]: fieldValue } as OfferContext;
      });
    },
    []
  );

  const handleJsonChange = useCallback((jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      isInternalUpdate.current = true;
      setLocalContext(parsed);
      setJsonError(null);
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Invalid JSON';
      setJsonError(errorMessage);
    }
  }, []);

  // Render availability override section (common to all strategies)
  const renderOverrideSection = () => {
    const ctx = (localContext || {}) as Record<string, unknown>;
    // Use != null to catch both null and undefined (null from DB, undefined from missing key)
    const hasOverride = ctx.available_override != null;
    const overrideReason = typeof ctx.override_reason === 'string' ? ctx.override_reason : '';

    return (
      <div className="override-section">
        <h4>Availability Override (Optional)</h4>
        <div className="checkbox-field">
          <label>
            <input
              type="checkbox"
              checked={hasOverride}
              onChange={(e) => {
                const newValue = e.target.checked;
                isInternalUpdate.current = true;
                setLocalContext((prev) => {
                  if (!prev) {
                    return newValue ? { available_override: true } : {};
                  }
                  if (newValue) {
                    return { ...prev, available_override: true };
                  } else {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { available_override, override_reason, show_message_on_card, ...rest } =
                      prev as Record<string, unknown>;
                    return rest as OfferContext;
                  }
                });
              }}
            />
            <span>Override Partner-Level Availability</span>
          </label>
          <span className="field-hint">
            Grant or revoke access for this specific user, independent of partner-level settings
          </span>
        </div>

        {hasOverride && (
          <>
            <Select
              label="Override Type"
              value={ctx.available_override === true ? 'grant' : 'revoke'}
              onChange={(val) => {
                const boolValue = val === 'grant';
                isInternalUpdate.current = true;
                setLocalContext((prev) => {
                  if (!prev) return { available_override: boolValue };
                  return { ...prev, available_override: boolValue };
                });
              }}
              options={[
                { value: 'grant', label: 'Grant Access (Override to Available)' },
                { value: 'revoke', label: 'Revoke Access (Override to Unavailable)' },
              ]}
            />
            <Input
              label="Override Reason"
              value={overrideReason}
              onChange={(val) => updateField('override_reason', val || undefined)}
              placeholder="e.g., Beta tester early access"
              hint="Reason for this override (for audit purposes)"
            />
            <div className="checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={ctx.show_message_on_card === true}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    isInternalUpdate.current = true;
                    setLocalContext((prev) => {
                      if (!prev) return { show_message_on_card: newValue };
                      if (newValue) {
                        return { ...prev, show_message_on_card: true };
                      } else {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { show_message_on_card: _, ...rest } = prev as Record<
                          string,
                          unknown
                        >;
                        return rest as OfferContext;
                      }
                    });
                  }}
                />
                <span>Show message on reward card</span>
              </label>
              <span className="field-hint">
                Display the override reason on the reward card in the app
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  // Render form based on strategy
  const renderMigrationForm = () => {
    const ctx = (localContext || {}) as Partial<MigrationContext>;
    return (
      <div className="context-form">
        <Input
          label="Migration Source"
          value={ctx.migration_source || ''}
          onChange={(val) => updateField('migration_source', val)}
          placeholder="e.g., legacy_rewards"
        />
        <Input
          label="Original Offer ID"
          value={ctx.original_offer_id || ''}
          onChange={(val) => updateField('original_offer_id', val)}
          placeholder="Original ID from source system"
        />
        <Input
          label="Migrated At"
          type="text"
          value={ctx.migrated_at || ''}
          onChange={(val) => updateField('migrated_at', val)}
          placeholder="ISO timestamp"
        />
        {renderOverrideSection()}
      </div>
    );
  };

  const renderManualForm = () => {
    const ctx = (localContext || {}) as Partial<ManualContext>;
    return (
      <div className="context-form">
        <Input
          label="Assigned By"
          value={ctx.assigned_by || ''}
          onChange={(val) => updateField('assigned_by', val)}
          placeholder="admin@example.com"
          disabled
          hint="Automatically populated with your email address"
        />
        <TextArea
          label="Reason"
          value={ctx.reason || ''}
          onChange={(val) => updateField('reason', val)}
          placeholder="Reason for manual assignment"
          rows={3}
        />
        <Input
          label="Ticket ID (optional)"
          value={ctx.ticket_id || ''}
          onChange={(val) => updateField('ticket_id', val)}
          placeholder="e.g., TICKET-12345"
        />
        {renderOverrideSection()}
      </div>
    );
  };

  const renderAutoDefaultForm = () => {
    const ctx = (localContext || {}) as Partial<AutoDefaultContext>;
    const variantOptions = getVariantOptions(variants);

    return (
      <div className="context-form">
        <Input
          label="Source"
          value="auto_default"
          onChange={() => {}}
          disabled
          hint="Automatically set for auto-default assignments"
        />
        <Input
          label="Reward Key"
          value={ctx.reward_key || ''}
          onChange={(val) => updateField('reward_key', val)}
          placeholder="e.g., benepass-card, cgm-device"
        />
        <Select
          label="Reward Variant"
          value={ctx.reward_variant || ''}
          onChange={(val) => updateField('reward_variant', val)}
          options={variantOptions.filter((opt) => opt.value !== '')}
        />
        <Input
          label="Assigned At"
          type="text"
          value={ctx.assigned_at || new Date().toISOString()}
          onChange={() => {}}
          disabled
          hint="Automatically set when the offer is created"
        />
        {renderOverrideSection()}
      </div>
    );
  };

  const renderStructuredForm = () => {
    switch (strategy) {
      case 'MIGRATION':
        return renderMigrationForm();
      case 'MANUAL':
        return renderManualForm();
      case 'AUTO_DEFAULT':
        return renderAutoDefaultForm();
      default:
        return (
          <p className="no-form">No structured form available for this strategy. Use JSON mode.</p>
        );
    }
  };

  return (
    <div className="offer-context-editor">
      <div className="editor-mode-toggle">
        <button
          type="button"
          className={`btn-toggle ${editMode === 'structured' ? 'active' : ''}`}
          onClick={() => setEditMode('structured')}
        >
          Structured Form
        </button>
        <button
          type="button"
          className={`btn-toggle ${editMode === 'json' ? 'active' : ''}`}
          onClick={() => setEditMode('json')}
        >
          JSON Editor
        </button>
      </div>

      {editMode === 'structured' ? (
        renderStructuredForm()
      ) : (
        <div className="json-editor-container">
          <JsonEditor
            value={localContext ? JSON.stringify(localContext, null, 2) : '{}'}
            onChange={handleJsonChange}
          />
          {jsonError && <div className="json-error-message">Invalid JSON: {jsonError}</div>}
        </div>
      )}
    </div>
  );
};

export default OfferContextEditor;
